/**
 * Prest0n Portal Client — frontend API calls to Phase 5 portal endpoints.
 *
 * All calls require a Firebase auth token (getIdToken).
 * The portal is account-admin-only; non-migrated users get 404.
 */

import { getAuth } from 'firebase/auth';

// ─────────────────────────────────────────────────────────────────────────────
// Base URL
// ─────────────────────────────────────────────────────────────────────────────

function getPortalBase(): string {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net`;
}

async function getIdToken(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

async function portalFetch<T>(
  path: string,
  method: 'GET' | 'POST' = 'GET',
  body?: unknown
): Promise<T> {
  const token = await getIdToken();
  const url = `${getPortalBase()}/${path}`;

  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let errMsg = `HTTP ${res.status}`;
    try { const j = await res.json(); errMsg = j.error || errMsg; } catch {}
    throw new Error(errMsg);
  }

  return res.json() as Promise<T>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface PortalAccountData {
  success: boolean;
  accountId: string;
  profile: {
    accountId: string;
    displayName: string;
    firmName: string;
    accountType: 'solo' | 'firm' | 'enterprise' | 'testing';
    status: 'active' | 'suspended' | 'pending';
    defaultLanguage: string;
  } | null;
  config: {
    personaName: string;
    welcomeMessage: string;
    defaultLanguage: string;
    brandingTone: string;
    practiceAreas?: string[];
    enabledCapabilities: string[];
    allowedWorkflows: string[];
    disclaimers: string[];
    voiceEnabled: boolean;
    knowledgePolicy: 'account-only' | 'account-plus-shared';
    defaultExecutionMode?: string;
    /** Research posture policy (2026-09-02) — absent on accounts not yet retrofitted (the platform default applies). */
    researchPosture?: ResearchPostureView;
    budgetCaps?: {
      dailyTokenLimit?: number | null;
      monthlyTokenLimit?: number | null;
    };
    migrationEnabled: boolean;
    coreReleasePinned?: string | null;
    overlayVersion?: string | null;
  } | null;
  vm: {
    assigned: boolean;
    assignmentMode: 'shared' | 'dedicated';
    vmName?: string | null;
    zone?: string | null;
    environment: string;
    provisioningStatus: string;
    healthStatus?: string | null;
    coreVersion?: string | null;
    overlayVersion?: string | null;
  } | null;
  meta: {
    allCapabilities: string[];
    allWorkflows: string[];
    allPracticeAreas: string[];
  };
}

export interface PortalUsageData {
  success: boolean;
  accountId: string;
  period: { from: string; to: string };
  totals: {
    totalInputTokens: number;
    totalOutputTokens: number;
    totalCostUsd: number;
    turnCount: number;
  };
  daily: Array<{
    date: string;
    totalInputTokens?: number;
    totalOutputTokens?: number;
    totalCostUsd?: number;
    turnCount?: number;
  }>;
  recentSessions: Array<{
    sessionId: string;
    status: string;
    executionMode: string;
    createdAt: string | null;
    lastTurnAt: string | null;
    usage: {
      totalInputTokens: number;
      totalOutputTokens: number;
      totalCostUsd: number;
      turnCount: number;
    } | null;
  }>;
}

/** Research posture (2026-09-02, Miles ruling 2026-09-01) — mirrors functions/src/prest0nSession/researchPosture.ts. */
export type ResearchTierView = 'none' | 'web-search' | 'deep-research';
export type EscalationTriggerView = 'jurisdiction-specific' | 'time-sensitive' | 'high-stakes';
export interface ResearchPostureClassView {
  minimumTier: ResearchTierView;
  escalateWhen: EscalationTriggerView[];
  cite: boolean;
}
export interface ResearchPostureView {
  version: number;
  enabled: boolean;
  classes: Record<string, ResearchPostureClassView>;
  provenance: { source: 'platform-default' | 'owner'; updatedBy: string | null; updatedAt: string; version: number; note?: string };
}
export const RESEARCH_POSTURE_CLASSES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'compliance-privacy-regulatory', label: 'compliance, privacy & regulatory' },
  { id: 'legal', label: 'legal' },
  { id: 'tax-accounting', label: 'tax & accounting' },
  { id: 'medical-health', label: 'medical & health' },
  { id: 'financial-advice', label: 'financial advice' },
  { id: 'immigration', label: 'immigration' },
  { id: 'employment-hr', label: 'employment & HR' },
  { id: 'insurance-benefits', label: 'insurance & benefits' },
  { id: 'real-estate-title', label: 'real estate & title' },
];
export const RESEARCH_POSTURE_PERMISSION = 'org.policies.research_posture.edit';

export interface ConfigUpdatePayload {
  personaName?: string;
  welcomeMessage?: string;
  defaultLanguage?: string;
  brandingTone?: string;
  practiceAreas?: string[];
  enabledCapabilities?: string[];
  allowedWorkflows?: string[];
  disclaimers?: string[];
  voiceEnabled?: boolean;
  knowledgePolicy?: 'account-only' | 'account-plus-shared';
  /** Research posture edit — the server gates it on org.policies.research_posture.edit. */
  researchPosture?: {
    enabled?: boolean;
    classes?: Record<string, Partial<ResearchPostureClassView>>;
    note?: string;
  };
}

export interface ProfileUpdatePayload {
  firmName?: string;
  displayName?: string;
  defaultLanguage?: string;
  accountType?: 'solo' | 'firm' | 'enterprise' | 'testing';
}

export interface BudgetCapsPayload {
  dailyTokenLimit?: number | null;
  monthlyTokenLimit?: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// API functions
// ─────────────────────────────────────────────────────────────────────────────

/** Fetch the account's profile, runtime config, and VM assignment. */
export async function getPortalAccount(): Promise<PortalAccountData> {
  return portalFetch<PortalAccountData>('prest0nPortalGetAccount');
}

/** Fetch 30-day usage data and recent sessions. */
export async function getPortalUsage(): Promise<PortalUsageData> {
  return portalFetch<PortalUsageData>('prest0nPortalGetUsage');
}

/** Update the runtime config (capabilities, tone, persona, etc.). */
export async function updatePortalConfig(payload: ConfigUpdatePayload): Promise<{ success: boolean; updated: string[] }> {
  return portalFetch('prest0nPortalUpdateConfig', 'POST', payload);
}

/** Update the account profile (firm name, display name, etc.). */
export async function updatePortalProfile(payload: ProfileUpdatePayload): Promise<{ success: boolean; updated: string[] }> {
  return portalFetch('prest0nPortalUpdateProfile', 'POST', payload);
}

/** Set budget caps (daily/monthly token limits). */
export async function setPortalBudgetCaps(payload: BudgetCapsPayload): Promise<{ success: boolean; budgetCaps: BudgetCapsPayload }> {
  return portalFetch('prest0nPortalSetBudgetCaps', 'POST', payload);
}
